import { useState, useEffect, useCallback, type RefObject } from 'react';

export interface FloatingPosition {
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: 'bottom' | 'top';
}

interface UseFloatingPositionOptions {
    isOpen: boolean;
    gap?: number;
    maxHeight?: number;
}

export function useFloatingPosition(
    anchorRef: RefObject<HTMLElement | null>,
    { isOpen, gap = 6, maxHeight = 320 }: UseFloatingPositionOptions
): FloatingPosition | null {
    const [position, setPosition] = useState<FloatingPosition | null>(null);

    const updatePosition = useCallback(() => {
        const anchor = anchorRef.current;
        if (!anchor || !isOpen) {
            setPosition(null);
            return;
        }

        const rect = anchor.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - gap;
        const spaceAbove = rect.top - gap;
        const placement =
            spaceBelow < Math.min(maxHeight, 160) && spaceAbove > spaceBelow ? 'top' : 'bottom';
        const availableSpace = placement === 'bottom' ? spaceBelow : spaceAbove;

        setPosition({
            top: placement === 'bottom' ? rect.bottom + gap : rect.top - gap,
            left: rect.left,
            width: rect.width,
            maxHeight: Math.max(120, Math.min(maxHeight, availableSpace)),
            placement,
        });
    }, [anchorRef, gap, isOpen, maxHeight]);

    useEffect(() => {
        if (!isOpen) {
            setPosition(null);
            return;
        }

        updatePosition();

        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, updatePosition]);

    return position;
}
